import React, { useState, useEffect } from "react";
import "../styles/UploadPage.scss";
import { config } from "../config";
import { useFooterData } from "../Apis/useApiQueries";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const UploadPage = () => {
  const BASE_URL = config.BackendUrl;

  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [uploadMessage, setUploadMessage] = useState("");
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { treatments } = useFooterData();
  const queryClient = useQueryClient();

  // 🔹 Authenticate from local storage
  useEffect(() => {
    const storedPassword = localStorage.getItem("authPassword");
    if (storedPassword === config.correctPassword) {
      setIsAuthenticated(true);
    }
  }, []);

  // 🔹 Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setImages((prevImages) => [...prevImages, ...files]);
  };

  // 🔹 Remove selected image
  const removeImage = (indexToRemove) => {
    setImages(images.filter((_, index) => index !== indexToRemove));
  };

  // 🔹 Handle treatment selection
  const handleTreatmentChange = (e) => setSelectedTreatment(e.target.value);

  // 🔹 Upload images mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await fetch(`${BASE_URL}/upload`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error("Error uploading image");
      return response.json();
    },
    onSuccess: (data) => {
      setUploadMessage(data.message);
      setTimeout(() => setUploadMessage(""), 5000);
      setImages([]);
      setSelectedTreatment("");
      queryClient.invalidateQueries(["images", selectedTreatment]);
    },
    onError: () => {
      setError("Error uploading image");
    },
  });

  // 🔹 Deduplicate treatments list
  const deduplicateTreatments = (treatmentArray = []) => {
    if (!Array.isArray(treatmentArray)) return []; // ✅ Ensure it's an array

    const uniqueItems = new Map();
    treatmentArray.forEach((treatment) => {
      if (treatment.treatmentRef) {
        if (!uniqueItems.has(treatment.treatmentRef)) {
          uniqueItems.set(treatment.treatmentRef, treatment);
        }
      } else {
        uniqueItems.set(treatment._id, treatment);
      }
    });
    return Array.from(uniqueItems.values());
  };

  const treatmentList = deduplicateTreatments(treatments?.data || []);

  // 🔹 Handle image upload
  const handleUpload = (e) => {
    e.preventDefault();
    if (!selectedTreatment) return setError("Please select a treatment");
    if (images.length === 0)
      return setError("Please select at least one image");

    const formData = new FormData();
    images.forEach((image) => formData.append("images", image));
    formData.append("treatmentId", selectedTreatment);

    uploadMutation.mutate(formData);
  };

  // 🔹 Handle password authentication
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === config.correctPassword) {
      setIsAuthenticated(true);
      localStorage.setItem("authPassword", config.correctPassword);
    } else {
      setError("Incorrect password");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="password-container">
        <h2>אנא הכנס את הסיסמה על מנת להכנס</h2>
        <form onSubmit={handlePasswordSubmit} className="password-form">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter Password"
            required
          />
          <button type="submit">שלח</button>
        </form>
        {error && <p className="error-message">{error}</p>}
      </div>
    );
  }

  return (
    <div className="container">
      <h1>העלאת תמונות</h1>

      <form onSubmit={handleUpload} className="upload-form">
        <input
          type="file"
          accept="image/*"
          multiple
          id="file-upload"
          className="hidden-input"
          onChange={handleFileChange}
        />
        <label htmlFor="file-upload" className="custom-file-button">
          📂 בחר תמונות
        </label>

        <select value={selectedTreatment} onChange={handleTreatmentChange}>
          <option value="">בחר טיפול</option>
          {treatmentList.map((treatment) => (
            <option key={treatment._id} value={treatment.treatmentRef}>
              {treatment.treatmentRef || treatment.name}
            </option>
          ))}
        </select>

        <button
          type="submit"
          disabled={images.length === 0 || !selectedTreatment}
        >
          העלה תמונות
        </button>
      </form>

      {uploadMessage && <p className="message">{uploadMessage}</p>}
      {images.length > 0 && (
        <div>
          <h2>תמונות שנבחרו</h2>
          <div className="image-container-uploade-page">
            {images.map((image, index) => (
              <div key={index} className="image-preview">
                <img
                  src={URL.createObjectURL(image)}
                  alt={image.name}
                  style={{
                    maxWidth: "150px",
                    maxHeight: "150px",
                    margin: "5px",
                  }}
                />
                <button onClick={() => removeImage(index)}>❌ הסר</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
